import { useCallback, useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import { useFirebaseAuth } from '../components/global/FirebaseProvider/FirebaseProvider';
import { getConfigurations } from '../components/trashie/utils/service';
import { POSTHOG_PROPERTIES, TRACKER_EVENT_TYPES } from '../components/trashie/config/tracker';

// The auth `isLoading` flip (false→true→false) plus multiple hook instances
// would each GET /configurations/giftCardsSegments. Coalesce concurrent/
// duplicate fetches into a single in-flight request; once it resolves the
// cache clears so a genuinely-later fetch can still run.
let inFlightSegments = null;
const fetchSegmentsOnce = () => {
  if (inFlightSegments) return inFlightSegments;
  inFlightSegments = getConfigurations('giftCardsSegments')
    .finally(() => { inFlightSegments = null; });
  return inFlightSegments;
};

const useHasAccessToGiftCards = () => {
  const { userData, isLoading: isLoadingFirebaseAuth, isSignedIn } = useFirebaseAuth();
  const posthog = usePostHog();

  const [isLoadingGiftCardsSegments, setIsLoadingGiftCardsSegments] = useState(true);
  const [giftCardsSegments, setGiftCardsSegments] = useState({});
  const [posthogSent, setPosthogSent] = useState(false);

  const loadGiftCardsSegments = useCallback(async () => {
    setIsLoadingGiftCardsSegments(true);
    const { value, error } = await fetchSegmentsOnce();

    if (error) {
      setGiftCardsSegments({});
      setIsLoadingGiftCardsSegments(false);
      return;
    }

    setGiftCardsSegments(value);
    setIsLoadingGiftCardsSegments(false);
  }, []);

  const getHasAccessToGiftCards = useCallback(() => {
    const userSegments = userData?.segments || [];
    const {
      enabled: segmentsEnabled,
      segments,
    } = giftCardsSegments;

    if (!segmentsEnabled) {
      return true;
    }

    const segmentFound = segments.find(segment => userSegments.includes(segment));

    if (segmentFound && !posthogSent) {
      posthog.capture(
        TRACKER_EVENT_TYPES.GIFT_CARDS_SEGMENT_SET,
        {
          [POSTHOG_PROPERTIES.GIFT_CARDS_SEGMENT]: segmentFound,
          $set: {
            email: userData.email,
          },
        },
      );

      setPosthogSent(true);
    }

    return Boolean(segmentFound);
  }, [userData, giftCardsSegments, posthogSent]);

  useEffect(() => {
    if (!isLoadingFirebaseAuth && isSignedIn) {
      loadGiftCardsSegments();
    }
  }, [isLoadingFirebaseAuth, isSignedIn]);

  return {
    isLoading: isLoadingFirebaseAuth || isLoadingGiftCardsSegments,
    hasAccessToGiftCards: getHasAccessToGiftCards(),
  };
};

export default useHasAccessToGiftCards;
